import { useCallback, useContext, useEffect } from 'react';
import { ContextApi } from 'react-uforms';
import { MAGIC_STUDY_TYPE, Question } from '@ch/common/types/data';
import { useQuizSubmit } from './use-quiz-submit';
import { useDropdown } from './use-dropdown';
import { QuizContext } from '../quiz-context';
import { useData } from '../../use-data';

const getChildOptions = ({ options, value }) =>
  (Object.values(options.find(({ value: optionValue }) => optionValue === value) || {})?.find(item =>
    Array.isArray(item),
  ) || []) as Array<string>;

export const useQuizAutocomplete = (question: Question) => {
  const {
    identifier: name,
    sortStrategy,
    additional_value: additionalValue = null,
    file = '',
    chunkedFile = false,
    depends_on: dependsOn = '',
  } = question;
  const {
    data: { questions },
  } = useData();
  const { setQuestion, resetUserQuestions } = useContext(QuizContext);
  const api = useContext(ContextApi);

  const { selectStudyValue } = useQuizSubmit();
  const parentFile = questions.find(({ identifier: id }) => id === dependsOn);

  const { rawOptions: rawStudyOptions } = useDropdown({
    file: parentFile?.file,
    sortStrategy,
  });

  const { options: optionsDefault, isLoading, onFocus: onDropdownFocus } = useDropdown({
    file,
    sortStrategy,
  });

  const { getValue, setValue } = useContext(ContextApi);
  const value = getValue(name);

  const studyValue = getValue(dependsOn);
  const studyValueObject = selectStudyValue(dependsOn, studyValue);
  const studyType = studyValueObject?.study_type;

  const generateOptionsForStudy = useCallback(() => {
    if (studyType === MAGIC_STUDY_TYPE) {
      return [
        ...getChildOptions({ options: rawStudyOptions, value: studyValue }).map(option => ({
          value: option,
          label: option,
        })),
        ...(additionalValue && [{ value: additionalValue.value, label: additionalValue.value }]),
      ];
    } else {
      return [{ value: studyValueObject['name'], label: studyValueObject['name'] }];
    }
  }, [additionalValue, studyValue, studyType, rawStudyOptions, studyValueObject]);

  const options = studyValue ? generateOptionsForStudy() : optionsDefault;

  const clearValue = useCallback(() => {
    if (value && !options.find(({ value: optionValue }) => value === optionValue)) {
      setValue(name, '');
    }
  }, [value, options, name, setValue]);

  useEffect(() => {
    clearValue();
  }, [clearValue]);

  const onFocus = useCallback(() => {
    if (!studyValue) {
      onDropdownFocus();
    }
  }, [onDropdownFocus, studyValue]);

  useEffect(() => {
    const ref = { mounted: true };
    onDropdownFocus(ref);
    return () => {
      ref.mounted = false;
    };
  }, [file, onDropdownFocus]);

  const handleInputChange = useCallback(
    (input: string) => {
      if (input && chunkedFile) {
        const newFile = file.replace(/[^_]+(?=\.json)/, input[0].toLowerCase());

        setQuestion({ ...question, file: newFile });
        resetUserQuestions(api);
      }
    },
    [setQuestion, resetUserQuestions, chunkedFile, question, api, file],
  );

  return {
    onFocus,
    isLoading,
    options,
    handleInputChange,
  };
};
